import { VoiceUITool } from './types'
import { buildVoiceTools } from './buildVoiceTools'
import { GenNode } from '../types/carbon/models/GenNode'
import { FILTER_SYNTAX, SCREEN, describeFilter, rankVariables } from './tablesVoiceTools'
import { convertVariableToCodeframe, reorderAxisNode, removeAxisNode, type AxisMoveDirection } from '../utils/axisHelpers'
import { getNodeTitle } from '../utils/nodeTypes'

// The Edit Spec page as tools (Help pane live tours PR 2), in one registry
// that the voice assistant, the Help pane and a tour all use, as Tables does
// (tablesVoiceTools.ts). SpecEditor builds them only on the /editspec route:
// inside the dashboard's Edit Data overlay it registers nothing, because a
// registration there would replace the dashboard's tools and leave the
// dashboard with none once the overlay closed (useVoiceRegistration).
//
// Nothing here saves a table. run_spec opens the result on Tables as a
// preview, where the person keeps it with Save Table or drops it with
// Discard. The filter is a change the person agrees to in words (confirm
// 'ask', the same rule and the same `filter` argument as set_quick_filter,
// so the panes' confirmedByUser check works for it). Cancel throws the edits
// away, so it waits for the pane's Confirm click.
//
// EDIT_SPEC_CONTROL_COVERAGE at the end maps every control SpecEditor and
// AxisEditor show to its tool, or to SCREEN with the reason; a test fails on
// a control in neither (editSpecCoverage.test.tsx).

export type SpecAxis = 'side' | 'top'

export interface EditSpecAxisDeps {
  nodes: () => GenNode[]
  /** Replace the axis's items, as the axis editor's onChange does. */
  set: (nodes: GenNode[]) => void
  /** The axis's syntax as the server last wrote it for its items. */
  syntax: () => string
  error: () => string | null
}

export interface EditSpecVoiceToolDeps {
  /** The table being edited ('' for a new one) and its title. */
  table: () => { path: string; title: string }
  axis: (axis: SpecAxis) => EditSpecAxisDeps
  variables: {
    /** The variable tree as the sidebar shows it. */
    nodes: () => GenNode[]
    /** A variable's codes (Carbon varCodes; reads no case data). */
    loadCodes: (node: GenNode) => Promise<GenNode[]>
  }
  filter: {
    get: () => string
    set: (expression: string) => void
    /** Checks an expression with the server. Returns the error, or null when it is valid. */
    validate: (expression: string) => Promise<string | null>
  }
  weight: { get: () => string; set: (expression: string) => void }
  caseFilter: () => string
  /** What Run Spec does: the result opens on Tables as a preview. */
  run: () => void
  /** What Cancel does: clears the draft and goes back. */
  cancel: () => void
  /** What Settings does. */
  openSettings: () => void
}

export const EDIT_SPEC_TOOL_NAMES = [
  'read_spec',
  'find_variable',
  'add_variable_to_axis',
  'move_axis_item',
  'remove_axis_item',
  'set_spec_filter',
  'set_spec_weight',
  'open_spec_settings',
  'run_spec',
  'cancel_edit_spec',
] as const

const AXES: SpecAxis[] = ['side', 'top']
const AXIS_WORDS: Record<SpecAxis, string> = { side: 'rows', top: 'columns' }
const DIRECTIONS: AxisMoveDirection[] = ['up', 'down', 'top', 'bottom']
const DIRECTION_WORDS: Record<AxisMoveDirection, string> = { up: 'up one place', down: 'down one place', top: 'to the top', bottom: 'to the bottom' }
/** How long an axis tool waits for the server to write the axis's new syntax. */
const SYNTAX_WAIT_MS = 4000

const ok = (extra?: Record<string, unknown>) => JSON.stringify({ ok: true, ...extra })
const err = (message: string, extra?: Record<string, unknown>) => JSON.stringify({ error: message, ...extra })

async function waitFor(cond: () => boolean, timeoutMs: number): Promise<boolean> {
  const start = Date.now()
  while (!cond()) {
    if (Date.now() - start > timeoutMs) return false
    await new Promise((r) => setTimeout(r, 25))
  }
  return true
}

const axisArg = {
  type: 'string',
  enum: AXES,
  description: 'side = the rows (Side Axis Editor); top = the columns (Top Axis Editor).',
}
const itemArg = {
  type: 'string',
  description: 'The item: its id or title from read_spec, or its place on the axis counting from 1.',
}

interface VariableEntry {
  node: GenNode
  name: string
  question: string
}

function variablesIn(nodes: GenNode[]): VariableEntry[] {
  const out: VariableEntry[] = []
  const walk = (list: GenNode[]) => {
    for (const n of list) {
      if (n.type === 'Variable' && n.value1) {
        out.push({ node: n, name: n.value1, question: n.meta?.find((m) => m.key === 'question')?.value || n.value2 || '' })
      } else if (n.children?.length) {
        walk(n.children)
      }
    }
  }
  walk(nodes)
  return out
}

/** The codes a codeframe can hold: Code, Net and Arith rows. */
const usableCodes = (codes: GenNode[]) => codes.filter((c) => c.type === 'Code' || c.type === 'Net' || c.type === 'Arith')

/** Each axis's items, flattened, with the axis they are on. */
function itemsOn(nodes: GenNode[]): GenNode[] {
  const out: GenNode[] = []
  const walk = (list: GenNode[]) => {
    for (const n of list) {
      out.push(n)
      if (n.children?.length && n.type !== 'Codeframe') walk(n.children)
    }
  }
  walk(nodes)
  return out
}

function describeItem(n: GenNode) {
  const codes = (n.children ?? []).filter((c) => c.type !== 'Loading')
  return {
    id: String(n.id),
    title: getNodeTitle(n) || n.value1 || '',
    type: n.type ?? '',
    ...(n.type === 'Codeframe' || n.type === 'Variable' ? { variable: n.value1 ?? '' } : {}),
    ...(codes.length ? { rows: codes.slice(0, 12).map((c) => getNodeTitle(c) || c.value2 || c.value1 || ''), ...(codes.length > 12 ? { more_rows: codes.length - 12 } : {}) } : {}),
  }
}

/**
 * The item a person names: its id, its place on the axis (1 = first), or its
 * title or variable name, exactly and then in part. Top-level items first.
 */
function findItem(nodes: GenNode[], wanted: string): GenNode | undefined {
  const w = wanted.trim()
  if (!w) return undefined
  const all = itemsOn(nodes)
  const byId = all.find((n) => String(n.id) === w)
  if (byId) return byId
  if (/^\d+$/.test(w) && Number(w) >= 1 && Number(w) <= nodes.length) return nodes[Number(w) - 1]
  const lw = w.toLowerCase()
  const names = (n: GenNode) => [getNodeTitle(n), n.value1, n.value2].filter(Boolean).map((s) => String(s).toLowerCase())
  return all.find((n) => names(n).includes(lw)) ?? all.find((n) => names(n).some((s) => s.includes(lw)))
}

export function buildEditSpecVoiceTools(deps: EditSpecVoiceToolDeps): VoiceUITool[] {
  const axisOf = (value: unknown): SpecAxis | undefined => (value === 'side' || value === 'top' ? value : undefined)

  /** Which axes a variable is on, as find_variable reports it. */
  const onAxis = (name: string): string | undefined => {
    const on = AXES.filter((a) => itemsOn(deps.axis(a).nodes()).some((n) => (n.type === 'Codeframe' || n.type === 'Variable') && (n.value1 ?? '').toLowerCase() === name.toLowerCase()))
    return on.length === 2 ? 'both' : on[0]
  }

  const resolveVariable = (wanted: string): { entry?: VariableEntry; candidates?: Array<{ name: string; question: string }> } => {
    const all = variablesIn(deps.variables.nodes())
    const lw = wanted.trim().toLowerCase()
    const exact = all.find((v) => v.name.toLowerCase() === lw) ?? all.find((v) => v.question.toLowerCase() === lw)
    if (exact) return { entry: exact }
    const ranked = rankVariables(all, wanted).map((hit) => all.find((v) => v.name === hit.name)!).filter(Boolean)
    if (ranked.length === 1) return { entry: ranked[0] }
    return { candidates: ranked.slice(0, 5).map((v) => ({ name: v.name, question: v.question })) }
  }

  const codesOf = async (entry: VariableEntry) => usableCodes((await deps.variables.loadCodes(entry.node)) ?? [])

  /**
   * Set an axis's items and wait until the axis syntax shows them, so a
   * run_spec straight after runs what the screen shows.
   */
  const setAxis = async (axis: SpecAxis, nodes: GenNode[]) => {
    const a = deps.axis(axis)
    const before = a.syntax()
    a.set(nodes)
    await waitFor(() => a.syntax() !== before || !!a.error(), SYNTAX_WAIT_MS)
    return a.error()
  }

  const filterVariables = {
    tree: async () => deps.variables.nodes(),
    codes: async (name: string) => {
      const entry = variablesIn(deps.variables.nodes()).find((v) => v.name.toLowerCase() === name.toLowerCase())
      return entry ? deps.variables.loadCodes(entry.node) : []
    },
  }

  const section = 'Edit Spec'

  return [
    {
      name: 'read_spec',
      description:
        "Read the spec being edited: the items on the side axis (rows) and the top axis (columns), each with its id, title and variable, and the filter, weight and case filter. Use an item's id or title with move_axis_item and remove_axis_item.",
      meta: { category: 'read', section, keywords: ['spec', 'rows', 'columns', 'axis', 'what is in'] },
      parameters: { type: 'object', properties: {}, additionalProperties: false },
      execute: () => {
        const t = deps.table()
        return JSON.stringify({
          table: t.title || t.path || 'a new table',
          side: deps.axis('side').nodes().map(describeItem),
          top: deps.axis('top').nodes().map(describeItem),
          filter: deps.filter.get() || undefined,
          weight: deps.weight.get() || undefined,
          case_filter: deps.caseFilter() || undefined,
          note: 'Nothing on this page is saved until the person runs the spec and saves the table on Tables.',
        })
      },
    },
    {
      name: 'find_variable',
      description:
        "Find the job's variables in the Variable Tree by name or question. An empty query lists the first variables in tree order. Each result says whether it is already on an axis (on_axis). With codes: true each result also carries its codes.",
      meta: { category: 'read', section: 'Variable Tree', keywords: ['variable', 'question', 'search variables', 'gender', 'age'] },
      parameters: {
        type: 'object',
        properties: {
          query: { type: 'string', description: "Words for the variable, e.g. 'gender'. Empty lists the tree in order." },
          codes: { type: 'boolean', description: 'Also return each variable’s codes.' },
        },
        required: ['query'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const query = String(args.query ?? '').trim()
        const all = variablesIn(deps.variables.nodes())
        const hits = query ? rankVariables(all, query).map((h) => all.find((v) => v.name === h.name)!).filter(Boolean) : all
        const shown = hits.slice(0, 12)
        const variables = await Promise.all(
          shown.map(async (v) => {
            const axis = onAxis(v.name)
            const base = { name: v.name, question: v.question, ...(axis ? { on_axis: axis } : {}) }
            if (args.codes !== true) return base
            const codes = await codesOf(v)
            return { ...base, code_count: codes.length, codes: codes.slice(0, 20).map((c) => ({ code: c.value1 ?? '', label: c.value2 ?? '' })) }
          }),
        )
        return JSON.stringify({
          count: hits.length,
          variables,
          ...(hits.length === 0 ? { hint: query ? 'No variable matched. Try another word for the question.' : 'The variable tree has not loaded yet.' } : {}),
        })
      },
    },
    {
      name: 'add_variable_to_axis',
      description:
        "Add a variable to the rows (side) or columns (top) of the spec, with all its codes, as ticking it in the Variable Tree and pressing the axis's green + does. Name it as find_variable returns it. It is added at the end of the axis; move it with move_axis_item.",
      meta: { category: 'action', section: 'Axes', surface: 'search', keywords: ['add', 'rows', 'columns', 'side axis', 'top axis', 'banner', 'variable'] },
      parameters: {
        type: 'object',
        properties: {
          variable: { type: 'string', description: 'The variable name (or its question) from find_variable.' },
          axis: axisArg,
        },
        required: ['variable', 'axis'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const axis = axisOf(args.axis)
        if (!axis) return err('axis must be side (rows) or top (columns)')
        const wanted = String(args.variable ?? '')
        const { entry, candidates } = resolveVariable(wanted)
        if (!entry) {
          return candidates?.length
            ? err(`"${wanted}" matches more than one variable; name one of them`, { candidates })
            : err(`there is no variable "${wanted}" in this job's Variable Tree; look it up with find_variable`)
        }
        const codes = await codesOf(entry)
        if (codes.length === 0) return err(`${entry.name} has no codes to show as rows or columns`)
        const frame = convertVariableToCodeframe(entry.node, codes)
        const nodes = [...deps.axis(axis).nodes(), frame]
        const problem = await setAxis(axis, nodes)
        if (problem) return err(`${entry.name} was added but the axis has an error: ${problem}`)
        const label = entry.question ? `${entry.name} (${entry.question})` : entry.name
        return ok({ did: `added ${label} to the ${AXIS_WORDS[axis]}`, item: String(frame.id), axis, position: nodes.length })
      },
    },
    {
      name: 'move_axis_item',
      description: "Move an item on the rows or columns up, down, to the top or to the bottom of its group, as the item's ⋮ Move menu does.",
      meta: { category: 'action', section: 'Axes', surface: 'search', keywords: ['move', 'reorder', 'up', 'down', 'top', 'bottom', 'first', 'last'] },
      parameters: {
        type: 'object',
        properties: {
          axis: axisArg,
          item: itemArg,
          direction: { type: 'string', enum: DIRECTIONS, description: 'up or down one place, or to the top or bottom.' },
        },
        required: ['axis', 'item', 'direction'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const axis = axisOf(args.axis)
        if (!axis) return err('axis must be side (rows) or top (columns)')
        const direction = DIRECTIONS.find((d) => d === args.direction)
        if (!direction) return err('direction must be up, down, top or bottom')
        const nodes = deps.axis(axis).nodes()
        const item = findItem(nodes, String(args.item ?? ''))
        if (!item) return err(`there is no item "${String(args.item ?? '')}" on the ${AXIS_WORDS[axis]}`, { items: nodes.map(describeItem).map((i) => i.title) })
        const title = getNodeTitle(item) || item.value1 || 'it'
        const moved = reorderAxisNode(nodes, item.id, direction)
        if (JSON.stringify(itemsOn(moved).map((n) => n.id)) === JSON.stringify(itemsOn(nodes).map((n) => n.id))) {
          return ok({ did: `${title} is already ${direction === 'top' ? 'at the top' : direction === 'bottom' ? 'at the bottom' : 'as far as it goes'}`, item: String(item.id) })
        }
        const problem = await setAxis(axis, moved)
        if (problem) return err(`${title} was moved but the axis has an error: ${problem}`)
        return ok({ did: `moved ${title} ${DIRECTION_WORDS[direction]} of the ${AXIS_WORDS[axis]}`, item: String(item.id) })
      },
    },
    {
      name: 'remove_axis_item',
      description: "Remove an item from the rows or columns, as the item's ⋮ Remove does. It changes only this unsaved spec.",
      meta: { category: 'action', section: 'Axes', surface: 'search', keywords: ['remove', 'take off', 'drop', 'delete row', 'delete column'] },
      parameters: {
        type: 'object',
        properties: { axis: axisArg, item: itemArg },
        required: ['axis', 'item'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const axis = axisOf(args.axis)
        if (!axis) return err('axis must be side (rows) or top (columns)')
        const nodes = deps.axis(axis).nodes()
        const item = findItem(nodes, String(args.item ?? ''))
        if (!item) return err(`there is no item "${String(args.item ?? '')}" on the ${AXIS_WORDS[axis]}`, { items: nodes.map(describeItem).map((i) => i.title) })
        const title = getNodeTitle(item) || item.value1 || 'the item'
        const problem = await setAxis(axis, removeAxisNode(nodes, item.id))
        if (problem) return err(`${title} was removed but the axis has an error: ${problem}`)
        return ok({ did: `removed ${title} from the ${AXIS_WORDS[axis]}` })
      },
    },
    {
      name: 'set_spec_filter',
      description:
        "Set the spec's filter (the Filter field in Filter and Weighting) to the respondents an expression selects. A filter is never applied on your say-so: " +
        "called without confirmed, it checks the expression with the server and answers ask_user with what the filter means in the survey's own words, and changes nothing. " +
        'Ask the user that meaning as a question and stop. Call it again with the same filter and confirmed: true only after they say yes. ' +
        'An empty filter removes the filter. Never guess a variable: look it up with find_variable first. ' +
        FILTER_SYNTAX.replace('find_variable_codes', 'find_variable'),
      meta: { category: 'action', section: 'Filter and Weighting', confirm: 'ask', surface: 'search', keywords: ['filter', 'only', 'base', 'subset', 'respondents'] },
      parameters: {
        type: 'object',
        properties: {
          filter: { type: 'string', description: 'The filter expression, e.g. Q1(2)&AGE(1;2). Empty removes the filter.' },
          confirmed: { type: 'boolean', description: 'true only after the user said yes to this filter, in reply to the question its ask_user answer gave you.' },
        },
        required: ['filter'],
        additionalProperties: false,
      },
      execute: async (args) => {
        const expression = String(args.filter ?? '').trim()
        if (!expression) {
          // Removing the filter changes what the table counts, so it asks
          // first, as set_quick_filter's clear does on Tables.
          if (args.confirmed !== true) {
            return JSON.stringify({
              ask_user: true,
              filter: '',
              meaning: 'no filter',
              message: 'Put this to the user and stop: "Remove the filter from this spec?" Remove it only after they say yes, by calling set_spec_filter again with filter "" and confirmed: true.',
            })
          }
          deps.filter.set('')
          return ok({ did: 'removed the filter' })
        }
        const problem = await deps.filter.validate(expression)
        if (problem) return err(`the filter was not applied: ${problem}`, { hint: FILTER_SYNTAX })
        const meaning = await describeFilter(expression, filterVariables)
        if (args.confirmed !== true) {
          return JSON.stringify({
            ask_user: true,
            filter: expression,
            meaning,
            message: `Put this to the user and stop: "${meaning} — use this filter?" Apply only after they say yes, by calling set_spec_filter again with the same filter and confirmed: true.`,
          })
        }
        deps.filter.set(expression)
        return ok({ did: `set the filter to ${meaning}`, filter: expression, meaning })
      },
    },
    {
      name: 'set_spec_weight',
      description: "Weight the spec by a weight variable (the Weight field in Filter and Weighting), as ticking it and pressing Add weight does. An empty weight removes the weight.",
      meta: { category: 'action', section: 'Filter and Weighting', surface: 'search', keywords: ['weight', 'weighting', 'unweighted'] },
      parameters: {
        type: 'object',
        properties: { weight: { type: 'string', description: 'The weight variable, e.g. WeightCombo (written WeightCombo()). Empty removes the weight.' } },
        required: ['weight'],
        additionalProperties: false,
      },
      execute: (args) => {
        const raw = String(args.weight ?? '').trim()
        if (!raw) {
          deps.weight.set('')
          return ok({ did: 'removed the weight' })
        }
        const name = raw.replace(/\(\s*\)$/, '').trim()
        const known = itemsOn(deps.variables.nodes()).find((n) => n.type !== 'Folder' && (n.value1 ?? '').toLowerCase() === name.toLowerCase())
        if (!known?.value1) return err(`there is no variable "${name}" in this job's Variable Tree to weight by; look it up with find_variable`)
        const expression = `${known.value1}()`
        deps.weight.set(expression)
        return ok({ did: `weighted the spec by ${expression}`, weight: expression })
      },
    },
    {
      name: 'run_spec',
      description:
        'Run the spec, as Run Spec does: the result opens on the Tables page as a preview. Nothing is saved: there the person keeps it with Save Table, or puts the table back with Discard.',
      meta: { category: 'action', section, surface: 'search', keywords: ['run', 'run spec', 'preview', 'see the table', 'show result'] },
      parameters: { type: 'object', properties: {}, additionalProperties: false },
      execute: async () => {
        const side = deps.axis('side')
        const top = deps.axis('top')
        if (side.nodes().length === 0 && top.nodes().length === 0) return err('add a variable to the rows or columns first')
        // An axis whose items changed a moment ago may still be waiting for its syntax.
        await waitFor(() => (side.nodes().length === 0 || !!side.syntax()) && (top.nodes().length === 0 || !!top.syntax()), SYNTAX_WAIT_MS)
        const problem = side.error() || top.error()
        if (problem) return err(`the spec has an error to fix first: ${problem}`)
        deps.run()
        return ok({ did: 'ran the spec: the result opens on Tables as a preview, and nothing is saved' })
      },
    },
    ...buildVoiceTools({
      actions: [
        { name: 'open_spec_settings', label: 'open the Settings drawer for this spec (what the cells show, decimals, significance, titles)', section, surface: 'search', keywords: ['settings', 'cells', 'decimals', 'significance', 'titles'], run: deps.openSettings },
        {
          name: 'cancel_edit_spec',
          label: 'cancel Edit Spec: throw away the changes made here and go back to the table',
          section,
          keywords: ['cancel', 'back', 'discard', 'leave', 'close editor'],
          confirm: 'click',
          confirmScope: () => deps.table().path,
          run: deps.cancel,
        },
      ],
    }),
  ]
}

/**
 * Every control the Edit Spec page shows (SpecEditor and AxisEditor), by area
 * and accessible name, mapped to the tool that does its job, or to SCREEN
 * when it is left to the person on purpose (the reason is in the comment).
 * A name matches exactly; a key ending in " …" matches a control whose name
 * starts with its words and carries a value after them ("More actions for
 * Gender"). editSpecCoverage.test.tsx renders the page and fails on any
 * control missing here, or an entry no longer on the page.
 *
 * Not covered here: the Settings drawer and the Filter dialog Edit Spec
 * opens. They are the same components the Tables page covers, and on Edit
 * Spec the person works them.
 */
export const EDIT_SPEC_CONTROL_COVERAGE: Record<string, Record<string, string>> = {
  Header: {
    // The helper's own button: opening the agent pane is not a page tool.
    'Edit Spec helper': SCREEN,
    'Run Spec': 'run_spec',
    Settings: 'open_spec_settings',
    Cancel: 'cancel_edit_spec',
    // Only in the dashboard's Edit Data overlay, which registers no tools;
    // saving stays with the person.
    'Save Table': SCREEN,
  },
  'Filter and Weighting': {
    'Add filter': 'set_spec_filter',
    Filter: 'set_spec_filter',
    'Edit filter': 'set_spec_filter',
    'Clear filter': 'set_spec_filter',
    'Add weight': 'set_spec_weight',
    Weight: 'set_spec_weight',
    'Clear weight': 'set_spec_weight',
    // The case filter is an advanced setting the person types; read_spec reads it.
    'Case Filter': SCREEN,
    'Clear case filter': SCREEN,
  },
  'Variable Tree': {
    'Search variables...': 'find_variable',
    // Survey Data or AutoCoder Data, a Manager's choice: the tools build from the tree shown.
    'Data source': SCREEN,
    // Page layout: a person's eye, not a request.
    'Collapse sidebar': SCREEN,
  },
  'Axis editor': {
    'Add selected to Side Axis': 'add_variable_to_axis',
    'Add selected to Top Axis': 'add_variable_to_axis',
    Syntax: 'read_spec',
    // Typing axis syntax by hand stays with the person.
    'Edit Side Axis syntax': SCREEN,
    'Edit Top Axis syntax': SCREEN,
    Cancel: SCREEN,
    Apply: SCREEN,
    // The item's ⋮ menu, named after the item ("More actions for Gender").
    'More actions for …': 'move_axis_item',
  },
  // The item's ⋮ menu (AxisEditor), by item title.
  'Axis item menu': {
    Move: 'move_axis_item',
    Up: 'move_axis_item',
    Down: 'move_axis_item',
    Top: 'move_axis_item',
    Bottom: 'move_axis_item',
    // Wrapping an item in a filter, its bases and copies: the person's, in their dialogs.
    'Add Filter': SCREEN,
    'Edit Filter': SCREEN,
    'Add/Remove Bases': SCREEN,
    Copy: SCREEN,
    Remove: 'remove_axis_item',
  },
}
